/**
 * Coach Page
 *
 * Full-screen AI coaching chat interface for operators. Loaded outside
 * OperatorLayout (no tab bar) at /operator/coach and /operator/coach/:sessionId.
 *
 * Two views:
 * 1. Mode selector (no sessionId) -- 4 mode cards with smart time-of-day default
 * 2. Chat view (sessionId present) -- full chat with SSE streaming
 *
 * Design follows operator portal mobile-first patterns:
 * - 56px sticky header with back button and mode pill
 * - max-w-lg centered content
 * - Touch targets >= 44px
 * - Guild Green (#00674a) brand color
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  Sun,
  ClipboardCheck,
  BookOpen,
  HelpCircle,
  MessageCircle,
  Loader2,
  AlertTriangle,
  Shield,
  X,
  ExternalLink,
  Clock,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ApiError } from '../../lib/api';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';
import {
  useCreateSession,
  useEndSession,
  useCoachSessions,
  useCoachSession,
  useCoachUsage,
  useStreamMessage,
  coachKeys,
} from '../../hooks/useCoach';
import { useQueryClient } from '@tanstack/react-query';
import type { CoachSessionMode, CoachAction } from '@guild/shared';
import { StatusPillBubble } from './components/StatusPillBubble';

// ============================================
// CONSTANTS
// ============================================

const PRIVACY_DISMISSED_KEY = 'op_coachPrivacyDismissed';
const MAX_INPUT_LENGTH = 500;
const SESSION_TIMEOUT_MINUTES = 15;
const MAX_MESSAGES_PER_SESSION = 10;
/**
 * Phase 31 chunk I: minimum display time (ms) for the StatusPillBubble.
 * Prevents jarring flash when a tool returns in <100ms (e.g. a fast DB query).
 * If a clear-trigger fires before this elapses since the pill mounted, the
 * clear is delayed so the pill stays visible for at least this long.
 */
const STATUS_PILL_MIN_DISPLAY_MS = 200;

/** Mode metadata for the selector screen */
const MODE_OPTIONS: Array<{
  mode: CoachSessionMode;
  title: string;
  description: string;
  icon: typeof Sun;
}> = [
  {
    mode: 'pre_shift',
    title: 'Pre-Shift Prep',
    description: 'Review what to focus on today based on your recent performance.',
    icon: Sun,
  },
  {
    mode: 'debrief',
    title: 'Debrief',
    description: 'Talk through your latest assessment results and identify next steps.',
    icon: ClipboardCheck,
  },
  {
    mode: 'sop_help',
    title: 'SOP Help',
    description: 'Get help understanding a specific procedure or standard.',
    icon: BookOpen,
  },
  {
    mode: 'quiz_review',
    title: 'Quiz Review',
    description: 'Review recent quiz results and strengthen weak areas.',
    icon: HelpCircle,
  },
];

/** Smart default mode based on time of day */
function getDefaultMode(): CoachSessionMode {
  const hour = new Date().getHours();
  if (hour < 12) return 'pre_shift';
  if (hour < 17) return 'debrief';
  return 'quiz_review';
}

/** Map action types to navigation paths */
function getActionHref(action: CoachAction): string {
  switch (action.type) {
    case 'view_sop':
      return `/operator/sops/${action.id}`;
    case 'take_quiz':
      return `/operator/practice/quiz/${action.id}`;
    case 'view_goal':
      return `/operator/goals`;
    case 'view_training':
      return `/operator/content/${action.id}`;
    default:
      return '/operator/content';
  }
}

/** Format mode enum to display label */
function getModeLabel(mode: CoachSessionMode): string {
  switch (mode) {
    case 'pre_shift': return 'Pre-Shift';
    case 'debrief': return 'Debrief';
    case 'sop_help': return 'SOP Help';
    case 'quiz_review': return 'Quiz Review';
    default: return 'Coach';
  }
}

// ============================================
// LOCAL MESSAGE TYPE
// ============================================

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  actions?: CoachAction[];
  suggestedFollowups?: string[];
}

// ============================================
// SUB-COMPONENTS
// ============================================

/** Typing indicator with 3 animated dots */
function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      <div className="flex gap-1">
        <span
          className="h-2 w-2 rounded-full bg-guild-green/60"
          style={{ animation: 'coachBounce 1.2s infinite 0ms' }}
        />
        <span
          className="h-2 w-2 rounded-full bg-guild-green/60"
          style={{ animation: 'coachBounce 1.2s infinite 200ms' }}
        />
        <span
          className="h-2 w-2 rounded-full bg-guild-green/60"
          style={{ animation: 'coachBounce 1.2s infinite 400ms' }}
        />
      </div>
    </div>
  );
}

/**
 * Lightweight markdown renderer for coach messages.
 * Handles: **bold**, *italic*, numbered lists, bullet lists, headers (##).
 * Does NOT pull in react-markdown — keeps bundle small.
 */
function CoachMarkdown({ text }: { text: string }) {
  const blocks = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let key = 0;

  function flushList() {
    if (listItems.length > 0 && listType) {
      const Tag = listType;
      elements.push(
        <Tag
          key={key++}
          className={cn(
            'my-1 space-y-0.5',
            listType === 'ul' ? 'list-disc pl-5' : 'list-decimal pl-5',
          )}
        >
          {listItems}
        </Tag>,
      );
      listItems = [];
      listType = null;
    }
  }

  function inlineFormat(line: string): React.ReactNode {
    // Process bold (**text**) and italic (*text*) inline
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let idx = 0;

    while (remaining.length > 0) {
      // Bold: **text**
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      // Italic: *text* (but not **)
      const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);

      const boldIndex = boldMatch ? remaining.indexOf(boldMatch[0]) : Infinity;
      const italicIndex = italicMatch ? remaining.indexOf(italicMatch[0]) : Infinity;

      if (boldIndex === Infinity && italicIndex === Infinity) {
        parts.push(remaining);
        break;
      }

      if (boldIndex <= italicIndex && boldMatch) {
        if (boldIndex > 0) parts.push(remaining.slice(0, boldIndex));
        parts.push(<strong key={`b${idx++}`}>{boldMatch[1]}</strong>);
        remaining = remaining.slice(boldIndex + boldMatch[0].length);
      } else if (italicMatch) {
        if (italicIndex > 0) parts.push(remaining.slice(0, italicIndex));
        parts.push(<em key={`i${idx++}`}>{italicMatch[1]}</em>);
        remaining = remaining.slice(italicIndex + italicMatch[0].length);
      }
    }

    return parts.length === 1 ? parts[0] : <>{parts}</>;
  }

  for (const line of blocks) {
    // Bullet list item: - text or * text (but not **)
    const bulletMatch = line.match(/^\s*[-]\s+(.+)/);
    // Asterisk bullet: * text (but not ** bold)
    const asteriskBulletMatch = !bulletMatch ? line.match(/^\s*\*(?!\*)(.+)/) : null;
    // Numbered list item: 1. text
    const numberedMatch = line.match(/^\s*\d+\.\s+(.+)/);
    // Header: ## text or ### text
    const headerMatch = line.match(/^#{1,3}\s+(.+)/);

    const matchedBullet = bulletMatch || asteriskBulletMatch;

    if (matchedBullet) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listItems.push(<li key={key++}>{inlineFormat(matchedBullet[1].trim())}</li>);
    } else if (numberedMatch) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listItems.push(<li key={key++}>{inlineFormat(numberedMatch[1])}</li>);
    } else {
      flushList();
      if (headerMatch) {
        elements.push(
          <p key={key++} className="mt-1 font-semibold">
            {inlineFormat(headerMatch[1])}
          </p>,
        );
      } else if (line.trim() === '') {
        elements.push(<br key={key++} />);
      } else {
        elements.push(
          <span key={key++}>
            {inlineFormat(line)}
            {'\n'}
          </span>,
        );
      }
    }
  }
  flushList();

  return <div className="whitespace-pre-wrap break-words">{elements}</div>;
}

/** Single message bubble */
function MessageBubble({ message }: { message: ChatMessage }) {
  const navigate = useNavigate();
  const isCoach = message.role === 'assistant';

  return (
    <div
      className={cn(
        'flex w-full coach-msg-appear',
        isCoach ? 'justify-start' : 'justify-end',
      )}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed',
          isCoach
            ? 'bg-green-50 border-l-2 border-guild-green text-op-text-primary'
            : 'bg-gray-100 text-op-text-primary',
        )}
      >
        {/* Message text — render markdown for coach, plain for user */}
        {isCoach ? (
          <CoachMarkdown text={message.content} />
        ) : (
          <div className="whitespace-pre-wrap break-words">{message.content}</div>
        )}

        {/* Action chips */}
        {message.actions && message.actions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.actions.map((action, idx) => (
              <button
                key={`${action.type}-${action.id}-${idx}`}
                type="button"
                onClick={() => navigate(getActionHref(action))}
                className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl bg-white px-3 py-1.5 text-xs font-medium text-guild-green ring-1 ring-guild-green/20 transition-colors duration-150 active:bg-guild-green/5"
              >
                <ExternalLink size={12} strokeWidth={1.5} />
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Streaming message bubble that shows content as it arrives */
function StreamingBubble({ content }: { content: string }) {
  if (!content) return <TypingIndicator />;

  return (
    <div className="flex w-full justify-start">
      <div className="max-w-[85%] rounded-2xl border-l-2 border-guild-green bg-green-50 px-4 py-3 text-sm leading-relaxed text-op-text-primary">
        <CoachMarkdown text={content} />
      </div>
    </div>
  );
}

// ============================================
// MODE SELECTOR VIEW
// ============================================

function ModeSelector({
  onSelectMode,
  isCreating,
}: {
  onSelectMode: (mode: CoachSessionMode) => void;
  isCreating: boolean;
}) {
  const { data: sessionsData } = useCoachSessions();
  const { data: usage } = useCoachUsage();
  const defaultMode = useMemo(() => getDefaultMode(), []);

  const recentSessions = sessionsData?.slice(0, 3) ?? [];
  const atDailyLimit = usage
    ? usage.sessionsToday >= usage.maxSessionsPerDay
    : false;

  return (
    <div className="space-y-6">
      {/* Header text */}
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-guild-green/10">
          <MessageCircle size={28} strokeWidth={1.5} className="text-guild-green" />
        </div>
        <h2 className="mt-3 text-lg font-semibold text-op-text-primary">
          AI Coach
        </h2>
        <p className="mt-1 text-sm text-op-text-secondary">
          Choose a coaching mode to get started.
        </p>
      </div>

      {/* Daily limit warning */}
      {atDailyLimit && (
        <div className="rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
          <p className="text-sm font-medium text-amber-800">
            You have reached today's session limit ({usage?.maxSessionsPerDay} sessions).
            Sessions reset at midnight.
          </p>
        </div>
      )}

      {/* Mode cards */}
      <div className="grid grid-cols-2 gap-3">
        {MODE_OPTIONS.map((opt) => {
          const isDefault = opt.mode === defaultMode;
          const Icon = opt.icon;

          return (
            <button
              key={opt.mode}
              type="button"
              disabled={isCreating || atDailyLimit}
              onClick={() => onSelectMode(opt.mode)}
              className={cn(
                'relative flex flex-col items-start rounded-2xl bg-op-card p-4 text-left ring-1 transition-all duration-150',
                'min-h-[120px]',
                'active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2',
                'disabled:opacity-50 disabled:pointer-events-none',
                isDefault
                  ? 'ring-guild-green/30 shadow-sm'
                  : 'ring-black/[0.04]',
              )}
            >
              {isDefault && (
                <span className="absolute -top-2 right-3 rounded-full bg-guild-green px-2 py-0.5 text-[10px] font-semibold text-white">
                  Suggested
                </span>
              )}
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-guild-green/10">
                <Icon size={18} strokeWidth={1.5} className="text-guild-green" />
              </div>
              <p className="mt-3 text-sm font-semibold text-op-text-primary">
                {opt.title}
              </p>
              <p className="mt-1 text-xs text-op-text-secondary line-clamp-2">
                {opt.description}
              </p>
            </button>
          );
        })}
      </div>

      {/* Recent sessions */}
      {recentSessions.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-op-text-tertiary">
            Recent Sessions
          </p>
          <div className="space-y-2">
            {recentSessions.map((session) => (
              <Link
                key={session.id}
                to={`/operator/coach/${session.id}`}
                className="flex items-center justify-between rounded-xl bg-op-card p-3 ring-1 ring-black/[0.04] transition-colors duration-150 active:bg-gray-50"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-op-text-primary">
                    {getModeLabel(session.mode)}
                  </p>
                  {session.summary && (
                    <p className="mt-0.5 truncate text-xs text-op-text-secondary">
                      {session.summary}
                    </p>
                  )}
                </div>
                <span className="ml-2 flex-shrink-0 text-xs text-op-text-tertiary">
                  {new Date(session.startedAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// CHAT VIEW
// ============================================

function ChatView({
  sessionId,
  initialMessages,
  onEndSession,
  isSessionEnded,
  sessionSummary,
  startedAt,
  maxMessages,
  initialMessageCount,
  onSessionAutoClose,
}: {
  sessionId: string;
  initialMessages: ChatMessage[];
  onEndSession: () => void;
  isSessionEnded: boolean;
  sessionSummary: string | null;
  startedAt: string | null;
  maxMessages: number;
  initialMessageCount: number;
  onSessionAutoClose: (summary: string | null) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sendMessage = useStreamMessage();

  // --- State ---
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  /**
   * Phase 31.2.1 chunk 4 (#6): committed-so-far streamed text on a multi-step
   * ReAct run. When a 'thinking' event arrives AFTER text has already streamed
   * (text -> tool -> text -> tool), the current `streamingContent` is flushed
   * here so it stays visible while the status pill re-appears for the next tool
   * phase, instead of resetting `streamingContent` lossily. Rendered as static
   * text above the live streaming bubble / pill. Always '' on the Anthropic
   * path (no 'thinking' events). Cleared whenever `streamingContent` is reset
   * (message commit / error / new send / session switch) since the committed
   * assistant message then carries the full text.
   */
  const [preamble, setPreamble] = useState('');
  /**
   * Phase 31.2.1 chunk 4 (#6): mirror of `streamingContent` so `handleThinking`
   * can read the latest streamed text at flush time WITHOUT nesting a state
   * setter inside another setter's updater (which double-fires under
   * React.StrictMode and would double the flushed text). Kept in sync
   * imperatively (not via an effect) wherever `streamingContent` changes:
   * `appendStreamingToken` (+=), `resetStreamingContent` / `handleThinking` /
   * the session-switch effect (= '') — so the value is authoritative in the
   * same tick, independent of React commit/effect timing.
   */
  const streamingContentRef = useRef('');
  const [streamError, setStreamError] = useState<string | null>(null);
  const [messageCount, setMessageCount] = useState(initialMessageCount);
  const [isTimedOut, setIsTimedOut] = useState(false);
  /**
   * Phase 31 chunk I: thinking-status pill for the Gemini agent loop.
   * Null on the Anthropic path (backend never emits 'thinking' events).
   */
  const [thinkingStatus, setThinkingStatus] = useState<string | null>(null);
  const [privacyDismissed, setPrivacyDismissed] = useState(() => {
    try {
      return localStorage.getItem(PRIVACY_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Derived: messages remaining for countdown
  const messagesRemaining = Math.max(0, maxMessages - messageCount);
  const showCountdown = messagesRemaining <= 2 && messagesRemaining > 0 && !isSessionEnded && !isTimedOut;

  // Session timeout check on mount
  useEffect(() => {
    if (startedAt && !isSessionEnded) {
      const sessionStart = new Date(startedAt).getTime();
      const timeoutMs = SESSION_TIMEOUT_MINUTES * 60 * 1000;
      if (Date.now() - sessionStart > timeoutMs) {
        setIsTimedOut(true);
      }
    }
  }, [startedAt, isSessionEnded]);

  // --- Refs ---
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /**
   * Phase 31 chunk I: timestamp (ms) when the current thinkingStatus was
   * first set. Used by `clearThinkingStatus` to enforce
   * STATUS_PILL_MIN_DISPLAY_MS so sub-100ms tool returns don't flash the pill.
   * Null when no pill is currently shown.
   */
  const thinkingMountTimeRef = useRef<number | null>(null);
  /**
   * Phase 31 chunk I: pending setTimeout id for a debounced pill clear.
   * Tracked so we can cancel it if a new 'thinking' event arrives during the
   * debounce window (replace-not-stack semantics from useCoach JSDoc).
   */
  const thinkingClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Phase 31 chunk I: thinking-status helpers (200ms min-display debounce) ---
  const setThinkingStatusWithMount = useCallback((status: string) => {
    // Cancel any pending clear so a new pill text replaces, not stacks.
    if (thinkingClearTimerRef.current !== null) {
      clearTimeout(thinkingClearTimerRef.current);
      thinkingClearTimerRef.current = null;
    }
    // Only stamp mount time on the first show; subsequent updates (multiple
    // sequential tool calls) keep the original mount time so the min-display
    // window measures from the FIRST pill, not each update.
    if (thinkingMountTimeRef.current === null) {
      thinkingMountTimeRef.current = Date.now();
    }
    setThinkingStatus(status);
  }, []);

  /**
   * Phase 31.2.1 chunk 4 (#6): 'thinking' SSE handler. On a multi-step ReAct
   * run, a 'thinking' event can arrive AFTER text has already streamed (a
   * sentence -> tool -> another sentence -> tool). Before showing the pill,
   * flush any already-streamed `streamingContent` into the `preamble` buffer
   * (kept visible as static text) and reset live `streamingContent` to '' so
   * the `:842`-equivalent gate re-triggers and the pill re-appears for the next
   * tool phase. No-op flush on the first tool phase (streamingContent empty).
   * Anthropic path never emits 'thinking', so this never runs there.
   */
  const handleThinking = useCallback((status: string) => {
    const streamed = streamingContentRef.current;
    if (streamed) {
      // Flush already-streamed text into the preamble (stays visible) and clear
      // the live buffer so the pill re-appears for the next tool phase. Two
      // independent top-level setters — no nested setter (StrictMode-safe).
      setPreamble((buf) => buf + streamed);
      streamingContentRef.current = '';
      setStreamingContent('');
    }
    setThinkingStatusWithMount(status);
  }, [setThinkingStatusWithMount]);

  const clearThinkingStatus = useCallback(() => {
    // Already pending a clear — let the existing timer run.
    if (thinkingClearTimerRef.current !== null) return;
    const mountTime = thinkingMountTimeRef.current;
    // Nothing to clear.
    if (mountTime === null) {
      setThinkingStatus(null);
      return;
    }
    const elapsed = Date.now() - mountTime;
    if (elapsed >= STATUS_PILL_MIN_DISPLAY_MS) {
      thinkingMountTimeRef.current = null;
      setThinkingStatus(null);
      return;
    }
    // Defer the clear until min-display elapses.
    thinkingClearTimerRef.current = setTimeout(() => {
      thinkingClearTimerRef.current = null;
      thinkingMountTimeRef.current = null;
      setThinkingStatus(null);
    }, STATUS_PILL_MIN_DISPLAY_MS - elapsed);
  }, []);

  // Cleanup any pending clear timer on unmount / session change.
  useEffect(() => {
    return () => {
      if (thinkingClearTimerRef.current !== null) {
        clearTimeout(thinkingClearTimerRef.current);
        thinkingClearTimerRef.current = null;
      }
      thinkingMountTimeRef.current = null;
    };
  }, []);

  // Reset pill state when sessionId changes (session switch within ChatView).
  useEffect(() => {
    if (thinkingClearTimerRef.current !== null) {
      clearTimeout(thinkingClearTimerRef.current);
      thinkingClearTimerRef.current = null;
    }
    thinkingMountTimeRef.current = null;
    setThinkingStatus(null);
    setPreamble('');
    streamingContentRef.current = '';
    setStreamingContent(''); // keep state in lockstep with the ref (matches resetStreamingContent)
  }, [sessionId]);

  // Update messages when initialMessages change (e.g., session loaded)
  useEffect(() => {
    if (initialMessages.length > 0) {
      setMessages(initialMessages);
    }
  }, [initialMessages]);

  // Auto-scroll to bottom on new messages or streaming content
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, isStreaming]);

  /**
   * Phase 31.2.1 chunk 4 (#6): set live streamed text + keep the flush-time
   * mirror authoritative in the same tick (so a 'thinking' event that arrives
   * immediately after a token reads the correct value, independent of React
   * commit/effect timing). Reused by both send handlers' onToken callbacks.
   */
  const appendStreamingToken = useCallback((token: string) => {
    streamingContentRef.current += token;
    setStreamingContent((prev) => prev + token);
  }, []);

  /**
   * Phase 31.2.1 chunk 4 (#6): reset live streamed text + mirror together.
   * Reused at every point that clears `streamingContent` (send-start, message
   * commit, error). Does NOT touch `preamble` — callers decide that.
   */
  const resetStreamingContent = useCallback(() => {
    streamingContentRef.current = '';
    setStreamingContent('');
  }, []);

  // Focus input after streaming completes
  useEffect(() => {
    if (!isStreaming && !isSessionEnded && !isTimedOut) {
      inputRef.current?.focus();
    }
  }, [isStreaming, isSessionEnded, isTimedOut]);

  // --- Privacy banner dismiss ---
  const dismissPrivacy = useCallback(() => {
    setPrivacyDismissed(true);
    try {
      localStorage.setItem(PRIVACY_DISMISSED_KEY, 'true');
    } catch {
      // localStorage unavailable
    }
  }, []);

  // --- Send message ---
  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isStreaming || isSessionEnded || isTimedOut) return;

    // Haptic feedback on send (optional API, safe no-op if unsupported)
    navigator.vibrate?.(10);

    // Add user message to local state
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsStreaming(true);
    resetStreamingContent();
    setPreamble('');
    setStreamError(null);

    try {
      const result = await sendMessage(
        sessionId,
        trimmed,
        (token) => {
          // Phase 31 chunk I: first token after a 'thinking' event means the
          // agent has moved from tool-call to text streaming — clear pill.
          clearThinkingStatus();
          appendStreamingToken(token);
        },
        handleThinking,
      );

      // Add assistant message with full content + parsed data
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: result.content,
        actions: result.actions,
        suggestedFollowups: result.suggestedFollowups,
      };
      setMessages((prev) => [...prev, assistantMessage]);
      resetStreamingContent();
      // Phase 31.2.1 chunk 4 (#6): committed message carries the full text now.
      setPreamble('');

      // Update local message count from server response
      if (result.messageCount !== undefined) {
        setMessageCount(result.messageCount);
      }

      // Handle auto-close when session limit reached
      if (result.sessionEnded) {
        onSessionAutoClose(result.sessionSummary ?? result.content.slice(0, 500));
      }

      // Invalidate session and usage queries
      queryClient.invalidateQueries({ queryKey: coachKeys.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: coachKeys.usage() });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Connection interrupted. Tap to retry.';
      setStreamError(errorMsg);
      resetStreamingContent();
      setPreamble('');
    } finally {
      setIsStreaming(false);
      // Phase 31 chunk I: ensure pill is cleared on done/error (debounced).
      clearThinkingStatus();
    }
  }, [inputValue, isStreaming, isSessionEnded, isTimedOut, sessionId, sendMessage, queryClient, onSessionAutoClose, clearThinkingStatus, handleThinking, appendStreamingToken, resetStreamingContent]);

  // --- Send suggested follow-up (directly invokes send logic) ---
  const handleSuggestedFollowup = useCallback(
    async (text: string) => {
      if (isStreaming || isSessionEnded || isTimedOut) return;

      const trimmed = text.trim();
      if (!trimmed) return;

      // Add user message to local state
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: trimmed,
      };
      setMessages((prev) => [...prev, userMessage]);
      setInputValue('');
      setIsStreaming(true);
      resetStreamingContent();
      setPreamble('');
      setStreamError(null);

      try {
        const result = await sendMessage(
          sessionId,
          trimmed,
          (token) => {
            // Phase 31 chunk I: first token after a 'thinking' event means the
            // agent has moved from tool-call to text streaming — clear pill.
            clearThinkingStatus();
            appendStreamingToken(token);
          },
          handleThinking,
        );

        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: result.content,
          actions: result.actions,
          suggestedFollowups: result.suggestedFollowups,
        };
        setMessages((prev) => [...prev, assistantMessage]);
        resetStreamingContent();
        // Phase 31.2.1 chunk 4 (#6): committed message carries the full text now.
        setPreamble('');

        // Update local message count from server response
        if (result.messageCount !== undefined) {
          setMessageCount(result.messageCount);
        }

        // Handle auto-close when session limit reached
        if (result.sessionEnded) {
          onSessionAutoClose(result.sessionSummary ?? result.content.slice(0, 500));
        }

        queryClient.invalidateQueries({ queryKey: coachKeys.session(sessionId) });
        queryClient.invalidateQueries({ queryKey: coachKeys.usage() });
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Connection interrupted. Tap to retry.';
        setStreamError(errorMsg);
        resetStreamingContent();
        setPreamble('');
      } finally {
        setIsStreaming(false);
        // Phase 31 chunk I: ensure pill is cleared on done/error (debounced).
        clearThinkingStatus();
      }
    },
    [isStreaming, isSessionEnded, isTimedOut, sessionId, sendMessage, queryClient, onSessionAutoClose, clearThinkingStatus, handleThinking, appendStreamingToken, resetStreamingContent],
  );

  // --- Retry on error ---
  const handleRetry = useCallback(() => {
    // Remove the last user message and re-send
    setStreamError(null);
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUserMsg) {
      setMessages((prev) => prev.filter((m) => m.id !== lastUserMsg.id));
      setInputValue(lastUserMsg.content);
    }
  }, [messages]);

  // --- Handle form submit ---
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleSend();
    },
    [handleSend],
  );

  // Get suggested follow-ups from the last assistant message
  const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant');
  const suggestedFollowups =
    !isStreaming && !isSessionEnded
      ? lastAssistantMsg?.suggestedFollowups ?? []
      : [];

  return (
    <div className="flex h-[calc(100dvh-56px)] flex-col">
      {/* Privacy banner */}
      {!privacyDismissed && (
        <div className="border-b border-guild-green/10 bg-green-50 px-4 py-2.5">
          <div className="mx-auto flex max-w-lg items-start gap-2">
            <Shield size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-guild-green" />
            <p className="flex-1 text-xs text-op-text-secondary">
              Conversations are private. Your manager cannot see them.
            </p>
            <button
              type="button"
              onClick={dismissPrivacy}
              className="flex-shrink-0 p-1"
              aria-label="Dismiss privacy notice"
            >
              <X size={14} strokeWidth={1.5} className="text-op-text-tertiary" />
            </button>
          </div>
        </div>
      )}

      {/* Scrollable message list */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4"
        role="log"
        aria-label="Chat messages"
        aria-live="polite"
      >
        <div className="mx-auto max-w-lg space-y-3">
          {messages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}

          {/* Streaming indicator / in-progress message.
              Phase 31 chunk I: when the Gemini agent is mid-tool-call and we
              have a status string (but no tokens yet), render the pill instead
              of the streaming bubble. Once tokens arrive, fall through to the
              streaming bubble. Anthropic path: thinkingStatus stays null, so
              the StreamingBubble path runs identically to pre-Phase-31.

              Phase 31.2.1 chunk 4 (#6): on a multi-step ReAct run, `preamble`
              holds text that streamed before a later tool phase. Render it as
              static text ABOVE the pill / live bubble so it stays visible while
              the pill re-appears — instead of dropping it. Always '' on the
              Anthropic path, so the two gates below are unchanged there. */}
          {isStreaming && preamble && <StreamingBubble content={preamble} />}
          {isStreaming && thinkingStatus && !streamingContent && (
            <StatusPillBubble status={thinkingStatus} />
          )}
          {isStreaming && !(thinkingStatus && !streamingContent) && (
            <StreamingBubble content={streamingContent} />
          )}

          {/* Stream error */}
          {streamError && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl bg-red-50 px-4 py-3 ring-1 ring-red-200">
                <p className="text-sm text-red-700">{streamError}</p>
                <button
                  type="button"
                  onClick={handleRetry}
                  className="mt-2 text-sm font-semibold text-red-700 underline"
                >
                  Tap to retry
                </button>
              </div>
            </div>
          )}

          {/* Suggested follow-up pills */}
          {suggestedFollowups.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {suggestedFollowups.map((text, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSuggestedFollowup(text)}
                  className="min-h-[36px] rounded-full bg-guild-green/5 px-3 py-1.5 text-xs font-medium text-guild-green ring-1 ring-guild-green/15 transition-colors duration-150 active:bg-guild-green/10"
                >
                  {text}
                </button>
              ))}
            </div>
          )}

          {/* Session timed out card */}
          {isTimedOut && !isSessionEnded && (
            <div className="rounded-2xl bg-amber-50 p-4 ring-1 ring-amber-200">
              <div className="flex items-start gap-2.5">
                <Clock size={18} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-amber-600" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    Session timed out
                  </p>
                  <p className="mt-1 text-xs text-amber-700">
                    This session was idle for more than {SESSION_TIMEOUT_MINUTES} minutes and has expired.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/operator/coach')}
                className="mt-4 flex min-h-[44px] w-full items-center justify-center rounded-xl bg-guild-green px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 active:bg-guild-green/90"
              >
                Start New Session
              </button>
            </div>
          )}

          {/* Session wrap-up card */}
          {isSessionEnded && (
            <div className="rounded-2xl bg-op-card p-4 ring-1 ring-black/[0.04]">
              <p className="text-xs font-semibold uppercase tracking-wider text-op-text-tertiary">
                Session Complete
              </p>
              {sessionSummary && (
                <p className="mt-2 text-sm text-op-text-secondary">
                  {sessionSummary}
                </p>
              )}
              <button
                type="button"
                onClick={() => navigate('/operator/coach')}
                className="mt-4 flex min-h-[44px] w-full items-center justify-center rounded-xl bg-guild-green px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 active:bg-guild-green/90"
              >
                Start New Session
              </button>
            </div>
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area (pinned to bottom) */}
      {!isSessionEnded && !isTimedOut && (
        <div className="border-t border-gray-100 bg-op-card px-4 py-3 pb-safe">
          {/* Message countdown (visible when 2 or fewer messages remain) */}
          {showCountdown && (
            <div className="mx-auto mb-2 max-w-lg text-center">
              <span className="text-xs font-medium text-amber-600">
                {messagesRemaining === 1
                  ? '1 message left in this session'
                  : `${messagesRemaining} messages left`}
              </span>
            </div>
          )}
          <form
            onSubmit={handleSubmit}
            className="mx-auto flex max-w-lg items-end gap-2"
          >
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value.slice(0, MAX_INPUT_LENGTH))}
                placeholder="Type your message..."
                disabled={isStreaming}
                maxLength={MAX_INPUT_LENGTH}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 pr-12 text-sm text-op-text-primary placeholder:text-op-text-tertiary focus:border-guild-green focus:outline-none focus:ring-1 focus:ring-guild-green disabled:opacity-50"
                aria-label="Message input"
              />
              {/* Character counter (visible near limit) */}
              {inputValue.length > MAX_INPUT_LENGTH - 50 && (
                <span
                  className={cn(
                    'absolute bottom-1 right-3 text-[10px]',
                    inputValue.length >= MAX_INPUT_LENGTH
                      ? 'text-red-500'
                      : 'text-op-text-tertiary',
                  )}
                >
                  {inputValue.length}/{MAX_INPUT_LENGTH}
                </span>
              )}
            </div>
            <button
              type="submit"
              disabled={!inputValue.trim() || isStreaming}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl bg-guild-green text-white transition-colors duration-150 active:bg-guild-green/90 disabled:opacity-40 disabled:pointer-events-none"
              aria-label="Send message"
            >
              {isStreaming ? (
                <Loader2 size={18} strokeWidth={1.5} className="animate-spin" />
              ) : (
                <Send size={18} strokeWidth={1.5} />
              )}
            </button>
          </form>

          {/* End session button */}
          {messages.length > 1 && !isStreaming && (
            <div className="mx-auto mt-2 max-w-lg text-center">
              <button
                type="button"
                onClick={onEndSession}
                className="text-xs font-medium text-op-text-tertiary underline transition-colors duration-150 active:text-op-text-secondary"
              >
                End session
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN PAGE COMPONENT
// ============================================

export function CoachPage() {
  useDocumentTitle('AI Coach');

  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // --- Session state ---
  const [activeSessionId, setActiveSessionId] = useState<string | null>(urlSessionId ?? null);
  const [activeMode, setActiveMode] = useState<CoachSessionMode | null>(null);
  const [initialMessages, setInitialMessages] = useState<ChatMessage[]>([]);
  const [isSessionEnded, setIsSessionEnded] = useState(false);
  const [sessionSummary, setSessionSummary] = useState<string | null>(null);
  const [sessionStartedAt, setSessionStartedAt] = useState<string | null>(null);
  const [maxMessages, setMaxMessages] = useState(MAX_MESSAGES_PER_SESSION);
  const [initialMessageCount, setInitialMessageCount] = useState(1);
  const [createErrorType, setCreateErrorType] = useState<'daily_limit' | 'org_budget' | null>(null);

  // --- Session query (for direct URL navigation) ---
  const sessionQuery = useCoachSession(urlSessionId ?? null);

  // --- Mutations + query client ---
  const createSession = useCreateSession();
  const endSession = useEndSession();
  const queryClient = useQueryClient();

  // Sync URL param to state
  useEffect(() => {
    if (urlSessionId && urlSessionId !== activeSessionId) {
      setActiveSessionId(urlSessionId);
    }
  }, [urlSessionId, activeSessionId]);

  // Populate state from loaded session data (direct URL navigation)
  useEffect(() => {
    if (!sessionQuery.data || !urlSessionId) return;
    const session = sessionQuery.data;

    // Populate mode and session metadata
    if (!activeMode) {
      setActiveMode(session.mode);
      setSessionStartedAt(
        session.startedAt instanceof Date
          ? session.startedAt.toISOString()
          : String(session.startedAt),
      );
      setMaxMessages(MAX_MESSAGES_PER_SESSION);
      setInitialMessageCount(session.messageCount);

      // Check if session is already ended
      if (session.status === 'completed' || session.status === 'timeout') {
        setIsSessionEnded(true);
        setSessionSummary(session.summary);
      }

      // Convert loaded messages to ChatMessage format
      const loadedMessages: ChatMessage[] = session.messages
        .filter((m) => m.role !== 'system')
        .map((m) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          actions: m.actions ?? undefined,
          suggestedFollowups: m.suggestedFollowups ?? undefined,
        }));
      if (loadedMessages.length > 0) {
        setInitialMessages(loadedMessages);
      }
    }
  }, [sessionQuery.data, urlSessionId, activeMode]);

  // --- Handle mode selection ---
  const handleSelectMode = useCallback(
    async (mode: CoachSessionMode) => {
      setCreateErrorType(null);
      try {
        const sopId = searchParams.get('sopId') ?? undefined;
        const sectionId = searchParams.get('sectionId') ?? undefined;
        const result = await createSession.mutateAsync({
          mode,
          sopId: mode === 'sop_help' ? sopId : undefined,
          sectionId: ['quiz_review', 'sop_help'].includes(mode) ? sectionId : undefined,
        });

        setActiveSessionId(result.sessionId);
        setActiveMode(result.mode);
        setIsSessionEnded(false);
        setSessionSummary(null);
        setSessionStartedAt(result.startedAt);
        setMaxMessages(result.maxMessages ?? MAX_MESSAGES_PER_SESSION);
        setInitialMessageCount(result.messageCount ?? 1);

        // Set initial assistant message
        const firstMsg: ChatMessage = {
          id: `assistant-init-${Date.now()}`,
          role: 'assistant',
          content: result.firstMessage,
          suggestedFollowups: result.suggestedFollowups,
        };
        setInitialMessages([firstMsg]);

        // Update URL without full page reload
        navigate(`/operator/coach/${result.sessionId}`, { replace: true });
      } catch (err) {
        // Detect 429 error types for friendly UI
        if (err instanceof ApiError && err.status === 429) {
          if (err.code === 'ORG_BUDGET_EXCEEDED') {
            setCreateErrorType('org_budget');
          } else {
            setCreateErrorType('daily_limit');
          }
        }
        console.error('Failed to create session:', err);
      }
    },
    [createSession, navigate, searchParams],
  );

  // Auto-select mode from query params
  useEffect(() => {
    const modeParam = searchParams.get('mode') as CoachSessionMode | null;
    if (
      modeParam &&
      ['pre_shift', 'debrief', 'sop_help', 'quiz_review'].includes(modeParam) &&
      !activeSessionId &&
      !createSession.isPending
    ) {
      handleSelectMode(modeParam);
    }
    // Only run once on mount when mode param is present
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Handle session auto-close (from message limit) ---
  const handleSessionAutoClose = useCallback((summary: string | null) => {
    setIsSessionEnded(true);
    setSessionSummary(summary);
    // Invalidate queries so the sessions list updates
    queryClient.invalidateQueries({ queryKey: coachKeys.sessions() });
    queryClient.invalidateQueries({ queryKey: coachKeys.usage() });
  }, [queryClient]);

  // --- Handle end session ---
  const handleEndSession = useCallback(async () => {
    if (!activeSessionId) return;

    try {
      const result = await endSession.mutateAsync(activeSessionId);
      setIsSessionEnded(true);
      setSessionSummary(result.summary);
    } catch (err) {
      console.error('Failed to end session:', err);
    }
  }, [activeSessionId, endSession]);

  // --- Handle back navigation ---
  const handleBack = useCallback(() => {
    if (activeSessionId && !isSessionEnded) {
      // If in active session, go back to mode selector
      setActiveSessionId(null);
      setActiveMode(null);
      setInitialMessages([]);
      setSessionStartedAt(null);
      setCreateErrorType(null);
      navigate('/operator/coach', { replace: true });
    } else {
      // Go back to operator portal
      navigate('/operator/content');
    }
  }, [activeSessionId, isSessionEnded, navigate]);

  // ============================================
  // RENDER
  // ============================================

  const isInChat = !!activeSessionId;

  return (
    <div className="min-h-dvh bg-op-surface">
      {/* Global CSS for typing animation + message appear */}
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes coachBounce {
            0%, 60%, 100% { transform: translateY(0); }
            30% { transform: translateY(-4px); }
          }
          @keyframes coachMsgAppear {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .coach-msg-appear {
            animation: coachMsgAppear 0.25s ease-out both;
          }
        }
        .pb-safe { padding-bottom: env(safe-area-inset-bottom, 0px); }
      `}</style>

      {/* Sticky header */}
      <header className="sticky top-0 z-10 h-14 bg-op-surface/95 backdrop-blur-sm border-b border-gray-100">
        <div className="mx-auto flex h-full max-w-lg items-center justify-between px-4">
          {/* Back button */}
          <button
            type="button"
            onClick={handleBack}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl transition-colors duration-150 active:bg-gray-100 focus-visible:ring-2 focus-visible:ring-guild-green focus-visible:ring-offset-2"
            aria-label="Back to portal"
          >
            <ArrowLeft size={20} strokeWidth={1.5} className="text-op-text-primary" />
          </button>

          {/* Title */}
          <span className="text-sm font-semibold text-op-text-primary">
            AI Coach
          </span>

          {/* Mode pill (right side) */}
          {isInChat && activeMode ? (
            <span className="rounded-full bg-guild-green/10 px-2.5 py-1 text-xs font-medium text-guild-green">
              {getModeLabel(activeMode)}
            </span>
          ) : (
            <div className="min-w-[44px]" />
          )}
        </div>
      </header>

      {/* Content */}
      {isInChat && urlSessionId && initialMessages.length === 0 && sessionQuery.isLoading ? (
        /* Loading state when navigating to session via URL (no local data yet) */
        <div className="flex flex-1 items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="animate-spin text-guild-green" />
            <p className="text-sm font-medium text-op-text-secondary">
              Loading session...
            </p>
          </div>
        </div>
      ) : isInChat && urlSessionId && initialMessages.length === 0 && sessionQuery.isError ? (
        /* Error state when session not found or fetch failed */
        <div className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="mx-auto max-w-sm text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
              <AlertTriangle size={28} strokeWidth={1.5} className="text-red-500" />
            </div>
            <h2 className="mt-4 text-lg font-semibold text-op-text-primary">
              Session not found
            </h2>
            <p className="mt-2 text-sm text-op-text-secondary">
              This coaching session doesn't exist or is no longer available.
            </p>
            <button
              type="button"
              onClick={() => navigate('/operator/coach')}
              className="mt-6 flex min-h-[44px] w-full items-center justify-center rounded-xl bg-guild-green px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 active:bg-guild-green/90"
            >
              Back to Coach
            </button>
          </div>
        </div>
      ) : isInChat ? (
        <ChatView
          sessionId={activeSessionId}
          initialMessages={initialMessages}
          onEndSession={handleEndSession}
          isSessionEnded={isSessionEnded}
          sessionSummary={sessionSummary}
          startedAt={sessionStartedAt}
          maxMessages={maxMessages}
          initialMessageCount={initialMessageCount}
          onSessionAutoClose={handleSessionAutoClose}
        />
      ) : (
        <div className="mx-auto max-w-lg px-4 py-6">
          {/* Create session error — differentiated by type */}
          {createSession.isError && createErrorType === 'daily_limit' && (
            <div className="mb-4 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
              <div className="flex items-start gap-2.5">
                <Clock size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    You have used all your sessions for today.
                  </p>
                  <p className="mt-1 text-xs text-amber-700">
                    Come back tomorrow. In the meantime, try reviewing your SOPs or taking a practice quiz.
                  </p>
                </div>
              </div>
            </div>
          )}
          {createSession.isError && createErrorType === 'org_budget' && (
            <div className="mb-4 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-amber-600" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Coaching is paused for your organization this month.
                  </p>
                  <p className="mt-1 text-xs text-amber-700">
                    Your organization's coaching budget has been reached. It will reset at the start of next month.
                  </p>
                </div>
              </div>
            </div>
          )}
          {createSession.isError && !createErrorType && (
            <div className="mb-4 rounded-xl bg-red-50 p-4 ring-1 ring-red-200">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} strokeWidth={1.5} className="mt-0.5 flex-shrink-0 text-red-600" />
                <p className="text-sm text-red-700">
                  Something went wrong. Please try again.
                </p>
              </div>
            </div>
          )}

          <ModeSelector
            onSelectMode={handleSelectMode}
            isCreating={createSession.isPending}
          />

          {/* Loading overlay when creating */}
          {createSession.isPending && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-op-surface/80 backdrop-blur-sm">
              <div className="flex flex-col items-center gap-3">
                <Loader2 size={32} className="animate-spin text-guild-green" />
                <p className="text-sm font-medium text-op-text-secondary">
                  Starting session...
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
